import {
  SIGNATURE_PRICING_LIMITS as limits,
  UPLOAD_CREDITS,
} from '~/lib/constants/limits';

export type SignaturePlan = 'founder' | 'team';
export type SignatureBilling = 'monthly' | 'annual';
export interface SignatureSelection {
  plan: SignaturePlan;
  billing: SignatureBilling;
  seats: number;
}

export function signaturePricingEnabled() {
  return process.env.NEXT_PUBLIC_SIGNATURE_PRICING_ENABLED === 'true';
}

export function getSignatureQuote(selection: SignatureSelection) {
  const { plan, billing, seats } = selection;
  if (
    !['founder', 'team'].includes(plan) ||
    !['monthly', 'annual'].includes(billing) ||
    !Number.isSafeInteger(seats) ||
    (plan === 'founder'
      ? seats !== limits.FOUNDER_SEATS
      : seats < limits.TEAM_MIN_SEATS || seats > limits.TEAM_MAX_SEATS)
  )
    throw new Error('Invalid Signature plan selection');
  const annual = billing === 'annual';
  const unitBilledCents = annual
    ? limits.ANNUAL_SEAT_CENTS
    : limits.MONTHLY_SEAT_CENTS;
  const monthlyCredits = seats * limits.CREDITS_PER_SEAT;
  return {
    ...selection,
    name: plan === 'founder' ? 'Founder' : 'Team',
    unitMonthlyCents: annual
      ? unitBilledCents / limits.MONTHS_PER_YEAR
      : unitBilledCents,
    billedCents: unitBilledCents * seats,
    monthlyCredits,
    estimatedProjects: Math.floor(monthlyCredits / limits.PROJECT_CREDITS),
  };
}

export function signatureCheckoutUrl(selection: SignatureSelection) {
  getSignatureQuote(selection);
  return `/signature-checkout?plan=${selection.plan}&billing=${selection.billing}&seats=${selection.seats}`;
}

export function parseSignatureCheckoutIntent(
  value: unknown,
): SignatureSelection | undefined {
  if (typeof value !== 'string' || !value.startsWith('/signature-checkout?'))
    return;
  const match =
    /^\/signature-checkout\?plan=(founder|team)&billing=(monthly|annual)&seats=([1-9]\d*)$/.exec(
      value,
    );
  if (!match) return;
  const selection = {
    plan: match[1] as SignaturePlan,
    billing: match[2] as SignatureBilling,
    seats: Number(match[3]),
  };
  try {
    getSignatureQuote(selection);
    return selection;
  } catch {
    return;
  }
}

export const signatureCreditFaq = [
  {
    question: 'What do credits cover?',
    answer:
      'Credits cover AI planning, slide images, and video transitions. Usage depends on slide count, quality, and how many versions you generate.',
  },
  {
    question: 'How many credits do I get?',
    answer: `Founder includes ${limits.CREDITS_PER_SEAT.toLocaleString('en-US')} credits. Team adds ${limits.CREDITS_PER_SEAT.toLocaleString('en-US')} credits per seat to a shared pool. Credits refresh on your billing date each month with either billing option. Enterprise has a custom pool.`,
  },
  {
    question: 'How are project estimates calculated?',
    answer: `Each estimate uses ${limits.PROJECT_SLIDES} slides with Best-quality images, no video transitions, and a ${limits.REVISION_ALLOWANCE_PERCENT}% allowance for revisions: ${limits.PROJECT_CREDITS} credits per project, including planning and structure. Longer decks, video, and extra generations use more. Imports add ${UPLOAD_CREDITS.PER_SLIDE} credit per slide.`,
  },
  {
    question: 'Do monthly credits roll over?',
    answer:
      'No. Monthly credits do not roll over. Credits refresh on your billing date each month.',
  },
  {
    question: 'Can I buy more credits?',
    answer: `With an active plan, add ${limits.TOP_UP_CREDITS.toLocaleString('en-US')} credits for $${limits.TOP_UP_CENTS / 100}. Top-up credits expire ${limits.TOP_UP_EXPIRATION_DAYS} days after purchase.`,
  },
] as const;
